import React from 'react';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import PhoneOrEmailVerification from '../../../components/PhoneOrEmailVerification';
import {AppRoute} from '../../../models/AppRoute';
import {AccountScreenAction, RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import rewardsTheme from '../../../theme/rewardsTheme';
import apiService from '../../../transport/apiService';
import type {SignUpFailureReason} from '../../../transport/types';
import {VerificationFormData} from '../schemas/authSchemas';

// DIS-1757: one message per create-account failure. The wrong-code copy is for `code_invalid`
// only. Literals are copied from their source screens to avoid a screen-to-screen import.
const getSignUpErrorMessage = (reason: SignUpFailureReason): string => {
  switch (reason) {
    case 'code_invalid':
      return 'Wrong code entered. Please try again.';
    case 'code_expired':
      return "Code expired. Tap 'Resend code' to get a new one.";
    case 'max_attempts':
      return "Too many attempts. Tap 'Resend code' to get a new one.";
    case 'email_taken':
      return 'An account with this email already exists. Please sign in instead.';
    case 'underage':
      return 'You must be 18 or older to create an account';
    case 'password_too_weak':
      return 'This password is too weak or may be compromised. Please go back and choose a different password.';
    case 'in_progress':
      return 'Your account is already being created. Please wait a moment.';
    case 'phone_taken':
      return "This mobile number is already linked to an account. Please log in, or contact us if this isn't you.";
    case 'no_store':
      return 'No store selected. Please go back and choose a store.';
    case 'unknown':
      return 'Something went wrong, please try again';
    default: {
      // Exhaustiveness check: a new SignUpFailureReason without copy fails to compile.
      // Runtime fallback: a reason unknown to this build still gets the generic copy.
      const unhandled: never = reason;
      void unhandled;
      return 'Something went wrong, please try again';
    }
  }
};

const AccountPhoneVerificationScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_PHONE_VERIFICATION>) => {
    const {userStore} = useStores();

    const onSubmitHandler = async (
      values: VerificationFormData,
      {
        setError,
      }: {setError: (field: 'verification', error: {type: string; message: string}) => void},
    ) => {
      if (route.params?.action === AccountScreenAction.UPDATE) {
        await userStore.updateUserInfo(
          route.params.action,
          userStore.formValues,
          () => {
            userStore.afterUpdateUser();
          },
          error => {
            if (error?.status === 'PHONE_NUMBER_VERIFICATION_FAILED') {
              setError('verification', {
                type: 'manual',
                message: error.message,
              });
            }
          },
          {
            verificationCode: values.verification,
          },
        );
      } else {
        // Assume it's Create User Account flow
        const result = await userStore.signUpUser(values.verification);

        if (result.ok) {
          navigation.navigate(AppRoute.ACCOUNT_SUCCESS);
        } else {
          setError('verification', {
            type: 'manual',
            message: getSignUpErrorMessage(result.reason),
          });
        }
      }
    };

    return (
      <ThemeProvider theme={rewardsTheme}>
        <PhoneOrEmailVerification
          type="phone"
          // CREATE only: the UPDATE flow keeps its layout.
          centredOnWide={route.params?.action !== AccountScreenAction.UPDATE}
          phoneNumber={userStore.formValues?.phoneNumber}
          onSubmit={onSubmitHandler}
          onResend={async () => {
            const result = await apiService.sendMobileVerificationCode({
              phoneNumber: userStore.formValues?.phoneNumber,
            });

            return !!result;
          }}
          onBack={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
            }
          }}
        />
      </ThemeProvider>
    );
  },
);

export default AccountPhoneVerificationScreen;
